import {
  IPartFinderAttributeName,
  IPartFinderProductType,
  IPartFinderProductTypeComparison,
  IPartFinderProductTypePayload,
  IPartFinderProductTypeVersion,
} from 'companySettings/types';
import { mapRecordsFromServer } from 'core/mappers';
import { partfinitiClient } from 'core/services';
import { GetManyResponse, IRequestPayload } from 'core/types';
import { getQueryStringToSnakeCase, objectKeysSnakeCaseToCamelCase } from 'core/utils';

export const getPartFinderProductTypes = async (
  requestPayload?: IRequestPayload,
  signal?: AbortSignal
): Promise<GetManyResponse<IPartFinderProductType>> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/finder-product-type-list/list?${getQueryStringToSnakeCase(requestPayload)}`,
    signal
  );

  return mapRecordsFromServer<IPartFinderProductType>(data?.data, data?.recordsFiltered);
};

// One product type with the attributes and values of its working version, which the grid's rows
// leave out; null when the company has no such product type.
export const getPartFinderProductType = async (
  id: number,
  signal?: AbortSignal
): Promise<IPartFinderProductType | null> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/finder-product-type-list/list?${getQueryStringToSnakeCase({
      filters: { ids: [id] },
    })}`,
    signal
  );

  return (
    mapRecordsFromServer<IPartFinderProductType>(data?.data, data?.recordsFiltered).records?.[0] ??
    null
  );
};

// The names already used by attributes on the company's product types, for the name suggestions.
export const getPartFinderAttributeNames = async (
  signal?: AbortSignal
): Promise<IPartFinderAttributeName[]> => {
  const { data } = await partfinitiClient.get(
    '/part_v2/finder-product-type-list/attribute-names',
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data?.data ?? []);
};

export const savePartFinderProductType = async (
  partFinderProductType: IPartFinderProductTypePayload
): Promise<{ success: boolean; partFinderProductType: IPartFinderProductType }> =>
  partfinitiClient.postForm('/part_v2/finder-product-type-edit/save', { partFinderProductType });

export const removePartFinderProductTypes = async (ids: number[]): Promise<{ success: boolean }> =>
  partfinitiClient.postForm('/part_v2/finder-product-type-edit/remove', { ids });

// Publishes the drafts of the product types; one with no draft is left as it is.
export const publishPartFinderProductTypes = async (
  ids: number[]
): Promise<{ success: boolean; published: number }> =>
  partfinitiClient.postForm('/part_v2/finder-product-type-edit/publish', { ids });

export const getPartFinderProductTypeVersions = async (
  productTypeId: number,
  signal?: AbortSignal
): Promise<IPartFinderProductTypeVersion[]> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/finder-product-type-version-list/list?${getQueryStringToSnakeCase({
      productTypeId,
    })}`,
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data?.versions ?? []);
};

export const comparePartFinderProductTypeVersions = async (
  { fromVersionId, toVersionId }: { fromVersionId: number; toVersionId: number },
  signal?: AbortSignal
): Promise<IPartFinderProductTypeComparison> => {
  const { data } = await partfinitiClient.get(
    `/part_v2/finder-product-type-version-list/compare?${getQueryStringToSnakeCase({
      from: fromVersionId,
      to: toVersionId,
    })}`,
    signal
  );

  return objectKeysSnakeCaseToCamelCase(data?.comparison);
};
